import {
	AI_REVIEW_MAX_BYTES,
	type AIResponseDTO,
	type DocumentDTO,
	isAiReviewableContentType,
} from "@zplanem/responses"

/**
 * Pure decision logic for the AI review badge, split out so it's unit-testable
 * without pulling the component's data-fetching chain (api-client, react-query)
 * into the jest/ts-jest world.
 */

type AiBadgeEligibility =
	| "hidden"
	| "ineligible-size"
	| "ineligible-type"
	| "eligible"

/**
 * Whether (and why) the AI badge should appear for a document, BEFORE consulting
 * any review row.
 *
 * Gated on `canDownload`, not just `aiReadable` (ZPL-211): the review contains
 * verbatim quotes from the document, so its existence/state must follow the same
 * CASL "read" tier as downloading the document — owner OR (scope member AND
 * (doc owner OR grant)). List visibility (scope membership) is broader, so
 * keying only on `aiReadable` would expose the badge to viewers who cannot open
 * the file.
 */
export function aiReviewBadgeEligibility(
	document: DocumentDTO,
): AiBadgeEligibility {
	if (!document.aiReadable || !document.canDownload) return "hidden"
	if (document.sizeBytes != null && document.sizeBytes > AI_REVIEW_MAX_BYTES) {
		return "ineligible-size"
	}
	if (!isAiReviewableContentType(document.mimeType)) return "ineligible-type"
	return "eligible"
}

type AiReviewState = "none" | "reviewing" | "processed" | "failed"

/** Maps a review row (or its absence) to the pill state shown for an eligible doc. */
export function aiReviewStateOf(
	review: AIResponseDTO | null | undefined,
): AiReviewState {
	if (!review) return "none"
	if (review.failedAt) return "failed"
	if (review.processedAt) return "processed"
	return "reviewing"
}
